import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Aperture,
  CircleDot,
  Redo2,
  RotateCcw,
  Undo2
} from "lucide-react";
import { ControlPanel } from "./components/ControlPanel";
import { MobileNotice } from "./components/MobileNotice";
import { StudioCanvas } from "./components/StudioCanvas";
import {
  DEFAULT_STATE,
  OUTPUT_DIMENSIONS,
  PALETTES
} from "./constants";
import { useStudioHistory } from "./hooks/useStudioHistory";
import {
  detectVideoCapability,
  exportPng,
  exportVideo
} from "./lib/export";
import { nextSeed } from "./lib/random";
import {
  applyTemplate,
  buildShareUrl,
  createInitialState,
  createSavedPreset,
  loadPresets,
  randomizeVisuals,
  savePresets
} from "./lib/state";
import type {
  ExportProgress,
  OutputSize,
  RenderResult,
  SavedPreset,
  TemplateId,
  VideoCapability
} from "./types";

const EMPTY_CAPABILITY: VideoCapability = {
  format: null,
  codec: null,
  label: "Checking video support…"
};

export default function App() {
  const initialState = useMemo(() => createInitialState(), []);
  const history = useStudioHistory(initialState);
  const { state, setState } = history;
  const [locks, setLocks] = useState({ palette: false, motif: false });
  const [paused, setPaused] = useState(false);
  const [restartKey, setRestartKey] = useState(0);
  const [renderInfo, setRenderInfo] = useState<RenderResult | null>(null);
  const [videoSupport, setVideoSupport] = useState<{
    output: OutputSize;
    capability: VideoCapability;
  }>({
    output: initialState.output,
    capability: EMPTY_CAPABILITY
  });
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [exportActive, setExportActive] = useState(false);
  const [exportError, setExportError] = useState("");
  const [presets, setPresets] = useState<SavedPreset[]>(() => loadPresets());
  const [toast, setToast] = useState("");
  const exportController = useRef<AbortController | null>(null);
  const exportInFlight = useRef(false);
  const capability =
    videoSupport.output === state.output ? videoSupport.capability : EMPTY_CAPABILITY;

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((current) => (current === message ? "" : current)), 2400);
  }, []);

  useEffect(() => {
    let cancelled = false;
    detectVideoCapability(state.output).then((result) => {
      if (!cancelled) {
        setVideoSupport({ output: state.output, capability: result });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [state.output]);

  const handleTemplate = (template: TemplateId) => {
    setState((current) => applyTemplate(current, template));
  };

  const handleRandomize = () => {
    setState((current) => randomizeVisuals(current, locks));
  };

  const handleShuffle = () => {
    setState((current) => ({ ...current, seed: nextSeed() }));
  };

  const handleExportPng = async () => {
    if (exportInFlight.current) return;
    exportInFlight.current = true;
    setExportActive(true);
    setExportError("");
    try {
      await exportPng(state);
      notify("PNG exported");
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "PNG export failed.");
    } finally {
      exportInFlight.current = false;
      setExportActive(false);
    }
  };

  const handleExportVideo = async () => {
    if (exportInFlight.current || !capability.format || !capability.codec) return;
    exportInFlight.current = true;
    setExportActive(true);
    setExportError("");
    setProgress({
      phase: "preparing",
      currentFrame: 0,
      totalFrames: state.animation.duration * state.animation.fps,
      format: capability.format
    });
    const controller = new AbortController();
    exportController.current = controller;
    try {
      await exportVideo(state, capability, setProgress, controller.signal);
      notify(`${capability.format?.toUpperCase()} exported`);
      window.setTimeout(() => setProgress(null), 1200);
    } catch (error) {
      setProgress(null);
      if (error instanceof DOMException && error.name === "AbortError") {
        notify("Video export cancelled");
      } else {
        setExportError(error instanceof Error ? error.message : "Video export failed.");
      }
    } finally {
      exportController.current = null;
      exportInFlight.current = false;
      setExportActive(false);
    }
  };

  const handleSavePreset = (name: string) => {
    const next = [createSavedPreset(name, state), ...presets].slice(0, 30);
    setPresets(next);
    savePresets(next);
    notify(`Saved “${name}”`);
  };

  const handleDeletePreset = (id: string) => {
    const next = presets.filter((preset) => preset.id !== id);
    setPresets(next);
    savePresets(next);
  };

  const handleShare = async () => {
    const url = buildShareUrl(state);
    try {
      await navigator.clipboard.writeText(url);
      notify("Share link copied");
    } catch {
      setExportError("Clipboard access was blocked. Copy the URL from the address bar after sharing.");
      window.history.replaceState(null, "", url);
    }
  };

  const handleCopySeed = async () => {
    try {
      await navigator.clipboard.writeText(state.seed.toString(16).padStart(8, "0").toUpperCase());
      notify("Seed copied");
    } catch {
      notify("Seed ready: select it from the control");
    }
  };

  const dimensions = OUTPUT_DIMENSIONS[state.output];
  const motifLabel = state.style.motif.replace("-", " ");

  return (
    <>
      <div className="desktop-studio">
        <header className="app-header">
          <div className="brand-lockup">
            <span className="brand-glyph" aria-hidden="true">
              <Aperture size={20} />
            </span>
            <span>
              <strong>WAVELET AUDIO</strong>
              <small>BRAND STUDIO</small>
            </span>
          </div>

          <div className="header-meta" aria-label="Current output">
            <CircleDot size={13} aria-hidden="true" />
            LIVE SIGNAL
            <span>{state.output === "wide" ? "16:9" : "1:1"}</span>
          </div>

          <div className="history-actions">
            <button
              type="button"
              className="icon-button"
              onClick={history.undo}
              disabled={!history.canUndo}
              aria-label="Undo"
            >
              <Undo2 size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={history.redo}
              disabled={!history.canRedo}
              aria-label="Redo"
            >
              <Redo2 size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="reset-button"
              onClick={() => {
                history.reset(DEFAULT_STATE);
                setRestartKey((value) => value + 1);
              }}
            >
              <RotateCcw size={15} aria-hidden="true" />
              Reset
            </button>
          </div>
        </header>

        <main className="workspace">
          <section className="preview-workspace" aria-label="Canvas preview">
            <StudioCanvas
              state={state}
              paused={paused}
              restartKey={restartKey}
              onRenderInfo={setRenderInfo}
            />
            <div className="preview-status">
              <span>PNG {dimensions.width} × {dimensions.height} px</span>
              <span>10.0s seamless loop</span>
              <span>{PALETTES[state.style.palette].label}</span>
              <span>{motifLabel}</span>
              {renderInfo?.overflow ? <strong>Headline needs less copy</strong> : null}
            </div>
          </section>

          <ControlPanel
            state={state}
            setState={setState}
            locks={locks}
            onToggleLock={(key) => setLocks((current) => ({ ...current, [key]: !current[key] }))}
            onApplyTemplate={handleTemplate}
            onRandomize={handleRandomize}
            onShuffle={handleShuffle}
            paused={paused}
            onPause={() => setPaused((value) => !value)}
            onRestart={() => {
              setRestartKey((value) => value + 1);
              setPaused(false);
            }}
            onExportPng={handleExportPng}
            onExportVideo={handleExportVideo}
            capability={capability}
            progress={progress}
            exportActive={exportActive}
            onCancelExport={() => exportController.current?.abort()}
            exportError={exportError}
            presets={presets}
            onSavePreset={handleSavePreset}
            onLoadPreset={(preset) => {
              history.reset(preset.state);
              notify(`Loaded “${preset.name}”`);
            }}
            onDeletePreset={handleDeletePreset}
            onShare={handleShare}
            onCopySeed={handleCopySeed}
          />
        </main>
        <div className={`toast ${toast ? "is-visible" : ""}`} role="status" aria-live="polite">
          {toast}
        </div>
      </div>
      <div className="mobile-only">
        <MobileNotice />
      </div>
    </>
  );
}
